/**
 * Global smooth scrolling.
 *
 * ScrollSmoother takes over the page scroll and eases it, which needs the wrapper/content pair
 * declared in Views/Layout.cshtml. Touch devices keep their native scrolling (smoothTouch
 * defaults to false) because momentum scrolling there already feels right and overriding it
 * tends to fight the browser.
 *
 * This is imported before the scroll-reveal module so the smoother owns the scroll before any
 * ScrollTrigger is created against it.
 */

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const wrapper = document.getElementById("smooth-wrapper");
const content = document.getElementById("smooth-content");

// Fall back to native scrolling when the markup isn't there or the visitor has asked for less
// motion — hijacking the scroll is exactly the kind of movement that setting is about.
if (wrapper && content && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    const smoother = ScrollSmoother.create({
        wrapper,
        content,
        smooth: 1.2,
        effects: true,
    });

    // ScrollSmoother tracks its own eased scroll position separately from the real DOM
    // scrollTop. A native anchor jump (or window.scrollTo/scrollIntoView) changes the DOM value
    // out from under it without telling it, and a tick later the smoother re-asserts the
    // position it thinks is correct — which is what shows up as the page snapping back to the
    // top a moment after the jump. Every in-page scroll has to go through smoother.scrollTo()
    // instead, which also has the side effect of fixing the reveal animations: it eases the
    // scroll rather than jumping instantly, so ScrollTrigger passes through (and fires) every
    // section's trigger on the way, the same as scrolling there by hand would.
    function scrollToHash(hash, smooth) {
        if (!hash || hash === "#") {
            return;
        }

        let target;
        try {
            target = document.querySelector(hash);
        } catch {
            return; // Not a valid CSS selector (e.g. a hash that isn't an element id).
        }

        if (!target) {
            return;
        }

        // Measured live rather than read from the --header-height custom property: that
        // variable only describes the header's unscrolled height (see base.css), but the header
        // shrinks once the page has scrolled (Header.cshtml/header.js), and an in-page anchor
        // click virtually always happens after that point.
        const header = document.querySelector("[data-site-header]");
        const headerHeight = header ? header.getBoundingClientRect().height : 0;

        smoother.scrollTo(target, smooth, `top top-=${headerHeight}`);
    }

    // Clicking an in-page anchor: intercept it so the scroll (and everything ScrollTrigger does
    // along the way) is driven by the smoother instead of the browser's native jump.
    document.addEventListener("click", (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        const link = event.target.closest("a[href^='#']");
        if (!link) {
            return;
        }

        event.preventDefault();
        scrollToHash(link.getAttribute("href"), true);
        history.pushState(null, "", link.getAttribute("href"));
    });

    // Landing directly on a page with a hash in the URL (e.g. a link from another page) is the
    // same problem on page load: wait for the webfont so the target's position is measured
    // correctly, then jump the smoother straight there without animating.
    if (location.hash) {
        document.fonts.ready.then(() => scrollToHash(location.hash, false));
    }
}
